import React, { useState, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Input,
  Radio,
} from "@material-tailwind/react";
import { toast } from "react-toastify";
import { IoReload } from "react-icons/io5";

import { isWebLink } from "../utils/inputValidations";
import { SeoTags } from "..";
import { useWhatsappContext } from "../../contexts/WhatsappContextProvider";
import { phoneNumberSanitation } from "../utils/inputSanitation";
import { copyToClipboard } from "../utils/copyToClipboard";

const QrGenerator = () => {
  const { wabInfo } = useWhatsappContext();

  const [qrType, setQrType] = useState("whatsapp");
  const [link, setLink] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(wabInfo?.wabPhoneNumber || "");
  const [message, setMessage] = useState("");
  const [valid, setValid] = useState(true);
  const [fgColor, setFgColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");

  const qrRef = useRef(null);

  const resetStates = () => {
    setLink("");
    setPhoneNumber(wabInfo?.wabPhoneNumber || "");
    setMessage("");
    setValid(true);
    setFgColor("#000000");
    setBgColor("#ffffff");
    setShowPreviewModal(false);
    setGeneratedLink("");
  };

  const isValidPhoneNumber = (phone) => {
    // Basic phone validation - digits only, 10-15 characters
    const phoneRegex = /^\d{10,15}$/;
    return phoneRegex.test(phone.replace(/\D/g, ""));
  };

  const generateWhatsAppLink = (phone, msg) => {
    const cleanPhone = phone.replace(/\D/g, ""); // Remove non-digits
    const encodedMessage = encodeURIComponent(msg);
    return `https://wa.me/${cleanPhone}${msg ? `?text=${encodedMessage}` : ""}`;
  };

  const verifyLinkOnClick = () => {
    if (qrType === "whatsapp") {
      if (!isValidPhoneNumber(phoneNumber)) {
        setValid(false);
        toast.warn("Please enter a valid phone number (10-15 digits)");
        return;
      }
      setValid(true);
      const whatsappLink = generateWhatsAppLink(phoneNumber, message);
      setGeneratedLink(whatsappLink);
    } else {
      if (!isWebLink(link)) {
        setValid(false);
        toast.warn(
          "Please enter a valid web link (starting with http or https)"
        );
        return;
      }
      setValid(true);
      setGeneratedLink(link);
    }
  };

  const handleDownloadOnClick = () => {
    const svg = qrRef.current.querySelector("svg");
    if (svg) {
      const svgData = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([svgData], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qr-code-${qrType}.svg`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleQrTypeChange = (type) => {
    setQrType(type);
    setValid(true);
    setGeneratedLink(""); // Clear generated QR when switching types
  };

  return (
    <main className="w-full mt-5 mb-10 lg:my-1 space-y-5">
      <SeoTags title="QR Code Generator ‣ WaBM" />

      <div className="w-full space-y-2">
        <h1 className="text-2xl">QR Code Generator</h1>
        <h3>Generate and modify QR codes for any link or WhatsApp message</h3>
      </div>

      <div className="wbm-card-3 border-b-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="space-y-3">
            <h3>Select QR Code Type</h3>

            {/* Radio buttons for QR type selection */}
            <div className="flex gap-6 mb-4">
              <Radio
                name="qr-type"
                label="WhatsApp Message"
                checked={qrType === "whatsapp"}
                onChange={() => handleQrTypeChange("whatsapp")}
                color="green"
              />
              <Radio
                name="qr-type"
                label="Website Link"
                checked={qrType === "web"}
                onChange={() => handleQrTypeChange("web")}
                color="blue"
              />
            </div>

            {/* Conditional input fields based on QR type */}
            {qrType === "whatsapp" ? (
              <div className="space-y-3">
                <h3>Enter WhatsApp details to generate QR code</h3>
                <Input
                  label="Phone Number (with country code)"
                  placeholder="919876543210"
                  value={phoneNumber}
                  onChange={(e) =>
                    setPhoneNumber(phoneNumberSanitation(e.target.value))
                  }
                  className="mb-3"
                  color={valid || phoneNumber === "" ? "green" : "red"}
                />
                <Input
                  label="Message (optional)"
                  placeholder="Hello! How can I help you?"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="mb-3"
                  color="green"
                />
                <p className="text-sm text-gray-600">
                  Note: Enter phone number with country code (e.g., 919876543210
                  for India)
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <h3>Enter the website link to generate QR code</h3>
                <Input
                  label="Type your web link"
                  placeholder="https://wabm.in"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  className="mb-3"
                  color={valid || link === "" ? "blue" : "red"}
                />
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="gradient"
                color={qrType === "whatsapp" ? "green" : "blue"}
                onClick={verifyLinkOnClick}
              >
                Generate QR Code
              </Button>
              <button
                type="button"
                onClick={resetStates}
                className="my-button-style-cancel"
                title="Reset QR Code Generator"
              >
                <IoReload size={24} />
              </button>
            </div>
          </div>
          {generatedLink && (
            <div className="mx-auto">
              <div className="my-4 flex gap-4">
                <div className="flex flex-col">
                  <label className="mb-1 text-sm">Foreground Color</label>
                  <input
                    type="color"
                    value={fgColor}
                    onChange={(e) => setFgColor(e.target.value)}
                    className="w-16 h-10 border rounded"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="mb-1 text-sm">Background Color</label>
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="w-16 h-10 border rounded"
                  />
                </div>
              </div>

              <div className="text-sm text-gray-600 mb-2">
                <p>QR Code for: </p>
                <p
                  onClick={() => copyToClipboard(generatedLink)}
                  className={`${
                    qrType === "whatsapp" ? "text-green-600" : "text-blue-600"
                  } break-all`}
                >
                  {generatedLink}
                </p>
              </div>

              {qrType === "whatsapp" && (
                <div className="text-xs text-gray-500 mb-2">
                  This QR code will open WhatsApp with phone number:{" "}
                  {phoneNumber}
                  {message && ` and message: "${message}"`}
                </div>
              )}

              <div
                ref={qrRef}
                className="bg-white p-4 inline-block border rounded-lg shadow"
              >
                <QRCodeSVG
                  value={generatedLink}
                  size={200}
                  level="H"
                  bgColor={bgColor}
                  fgColor={fgColor}
                />
              </div>

              <div className="mt-4 flex gap-4">
                <Button
                  color={qrType === "whatsapp" ? "green" : "blue"}
                  onClick={handleDownloadOnClick}
                >
                  Download SVG
                </Button>
                <Button
                  color={qrType === "whatsapp" ? "green" : "blue"}
                  variant="outlined"
                  onClick={() => setShowPreviewModal(true)}
                >
                  Preview
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* QR Preview Modal */}
        <Dialog
          open={showPreviewModal}
          handler={() => setShowPreviewModal(false)}
          size="sm"
        >
          <DialogHeader>
            {qrType === "whatsapp" ? "WhatsApp" : "Website"} QR Code Preview
          </DialogHeader>
          <DialogBody className="flex flex-col items-center space-y-3">
            <QRCodeSVG
              value={generatedLink}
              size={250}
              level="H"
              bgColor={bgColor}
              fgColor={fgColor}
            />
            <div className="text-center text-sm text-gray-600 break-all">
              {generatedLink}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="text"
              color="red"
              onClick={() => setShowPreviewModal(false)}
              className="mr-2"
            >
              Close
            </Button>
            <Button
              color={qrType === "whatsapp" ? "green" : "blue"}
              onClick={handleDownloadOnClick}
            >
              Download
            </Button>
          </DialogFooter>
        </Dialog>
      </div>
    </main>
  );
};

export default QrGenerator;
